import { addMinutes } from "date-fns";
import { MeetingFormData, CalendarPlatform } from "../types/meeting.js";

/**
 * Parses date and time strings into a Date object
 */
export function parseFormDateTime(startDate: string, startTime: string): Date {
  return new Date(`${startDate}T${startTime}:00`);
}

/**
 * Builds the event data payload for API submission
 */
export function buildEventData(
  formData: MeetingFormData,
  attendees: string[]
) {
  const startDateTime = parseFormDateTime(formData.startDate, formData.startTime);
  const endDateTime = addMinutes(startDateTime, formData.duration);

  return {
    summary: formData.title,
    description: formData.description,
    start: startDateTime.toISOString(),
    end: endDateTime.toISOString(),
    location: formData.isOnline ? "ONLINE" : formData.location,
    attendees: attendees,
  };
}

/**
 * Determines the correct API endpoint based on platform
 */
export function getApiEndpoint(platform: CalendarPlatform): string {
  switch (platform) {
    case "google":
      return "/api/unifiedtime/google/create";
    case "microsoft":
      return "/api/unifiedtime/microsoft/create";
    case "professional-email":
      return "/api/unifiedtime/professional-email/create";
  }
}

/**
 * Validates required form fields
 */
export function validateFormData(formData: MeetingFormData): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!formData.title.trim()) {
    errors.push("Meeting title is required");
  }

  if (!formData.startDate) {
    errors.push("Start date is required");
  }

  if (!formData.startTime) {
    errors.push("Start time is required");
  }

  if (formData.duration <= 0) {
    errors.push("Duration must be greater than 0");
  }

  if (!formData.isOnline && !formData.location.trim()) {
    errors.push("Location is required for in-person meetings");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Parses comma-separated email string into array
 */
export function parseEmailsFromCsv(emailString: string): string[] {
  return emailString
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
}

/**
 * Converts email array to comma-separated string
 */
export function emailsToCsv(emails: string[]): string {
  return emails.join(", ");
}

/**
 * Merges two email lists, removing duplicates
 */
export function mergeEmailLists(list1: string[], list2: string[]): string[] {
  const combined = [...list1, ...list2];
  return Array.from(new Set(combined));
}
